/**
 * web/src/lib/icons.tsx
 * Lucide icon mapping for the 25 names referenced in the design handoff
 * (planning-docs/design_handoff_pixel_dashboard/design_files/icons.jsx).
 * The handoff explicitly grants latitude on iconography (README §Fidelity);
 * we pick the closest Lucide equivalent at the same default size (16px).
 *
 * Usage:
 *   <Icon name="workflow" size={16} />
 * or import the lucide-react component directly when only one is needed.
 *
 * Maintenance contract: every name in the handoff prototype's `Icon` switch
 * MUST have an entry here so AppShell + page-level icon usages stay
 * pixel-faithful when ported to the dashboard.
 */
import type { SVGAttributes } from 'react';
import {
  GitBranch, // workflow
  CheckCircle, // approval / check (filled use)
  MailCheck, // confirm
  Calendar, // event
  Settings, // settings
  HelpCircle, // help
  LogOut, // logout
  Plus, // plus
  RotateCw, // retry
  Search, // search
  Filter, // filter
  ArrowDownUp, // sort
  Check, // check
  X, // x
  AlertTriangle, // alert
  Info, // info
  ChevronRight, // chevron-right
  ChevronDown, // chevron-down
  ExternalLink, // external
  Copy, // copy
  MoreHorizontal, // more
  ArrowLeft, // arrow-left
  Mail, // mail
  User, // user
  Building2, // building
  Clock, // time
  Hourglass, // hourglass (event-wait timeline marker)
  Send, // send (Approve & Send buttons)
  Trash2, // trash (admin delete affordances)
  Bold, // bold (email body editor toolbar)
  Italic, // italic (email body editor toolbar)
  Underline, // underline (email body editor toolbar)
  List, // list (email body editor toolbar — bullet list)
  ListOrdered, // list-ordered (email body editor toolbar — numbered list)
  Link2, // link (email body editor toolbar)
  Unlink2, // unlink (email body editor toolbar)
  Paperclip, // paperclip (email gate attachments)
  Archive, // archive (soft-hide a workflow)
  ArchiveRestore, // unarchive (restore a hidden workflow)
  Mic, // speaker (Speakers workload nav)
  Upload, // upload (asset-received trigger chip)
  Users, // users (quorum trigger chip)
  Handshake, // sponsor (Sponsors workload nav)
  Play, // play (roster Start onboarding CTA)
  Download, // download (roster Export)
  Shuffle, // shuffle (card gate — random highlight color swatch)
  Sparkles, // sparkles (card gate — Create final portrait CTA)
  Maximize2, // maximize (card gate — expand a draft to review detail)
} from 'lucide-react';

/** Name → Lucide component. Keys match the 25 names in the handoff. */
export const ICONS = {
  workflow: GitBranch,
  approval: CheckCircle,
  confirm: MailCheck,
  event: Calendar,
  settings: Settings,
  help: HelpCircle,
  logout: LogOut,
  plus: Plus,
  retry: RotateCw,
  search: Search,
  filter: Filter,
  sort: ArrowDownUp,
  check: Check,
  x: X,
  alert: AlertTriangle,
  info: Info,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  external: ExternalLink,
  copy: Copy,
  more: MoreHorizontal,
  'arrow-left': ArrowLeft,
  mail: Mail,
  user: User,
  building: Building2,
  time: Clock,
  hourglass: Hourglass,
  send: Send,
  trash: Trash2,
  bold: Bold,
  italic: Italic,
  underline: Underline,
  list: List,
  'list-ordered': ListOrdered,
  link: Link2,
  unlink: Unlink2,
  paperclip: Paperclip,
  archive: Archive,
  'archive-restore': ArchiveRestore,
  speaker: Mic,
  upload: Upload,
  users: Users,
  sponsor: Handshake,
  play: Play,
  download: Download,
  shuffle: Shuffle,
  sparkles: Sparkles,
  maximize: Maximize2,
} as const;

/** Strongly-typed icon name — keeps AppShell + page usages honest. */
export type IconName = keyof typeof ICONS;

/** Public props for the Icon convenience component. */
export interface IconProps extends Omit<SVGAttributes<SVGElement>, 'name'> {
  name: IconName;
  /** Pixel size (square). Defaults to 14 — matches handoff inline-svg sizing. */
  size?: number;
  className?: string;
}

/**
 * Renders a Lucide icon by handoff name.
 *
 * @param name one of the 25 handoff icon keys
 * @param size pixel width/height (default: 14)
 * @param className optional Tailwind classes (color via text-* utility)
 * @sideEffects none — purely visual
 * @idempotent yes
 */
export function Icon({ name, size = 14, className, ...rest }: IconProps) {
  const Cmp = ICONS[name];
  return <Cmp width={size} height={size} className={className} {...rest} />;
}
